import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import AddSubject from '../components/AddSubject';
import { addSubject, getSubjects, updateSubject, deleteSubject } from '../lib/api';

const customStyles = `
  .table-image {
    width: 50px;
    height: 50px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
  }
  
  .image-placeholder {
    width: 50px;
    height: 50px;
    background: #f3f4f6;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
    font-size: 12px;
  }
`;

const SubjectList = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [subjectToEdit, setSubjectToEdit] = useState(null);
  const [showEntries, setShowEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Get token
  const getToken = () => {
    return localStorage.getItem('authToken') || localStorage.getItem('token');
  };

  // Fetch subjects from API
  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await getSubjects(token);

      if (response.status === 'success') {
        setSubjects(response.data || []);
      } else {
        console.error('Failed to fetch subjects:', response.message);
        setSubjects([]);
      }
    } catch (error) {
      console.error('Failed to fetch subjects:', error);
      setSubjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubjects();
  }, []);

  const toggleDrawer = (subject = null) => {
    setSubjectToEdit(subject);
    setIsDrawerOpen(!isDrawerOpen);
  };

  // Handle form submission
  const handleSubmit = async (payload, isUpdate, imageFile = null) => {
    try {
      const token = getToken();
      const formData = new FormData();

      formData.append("SubjectName", payload.SubjectName);
      formData.append("IsActive", String(payload.IsActive));
      formData.append("SubjectNameOtherLang", payload.SubjectNameOtherLang);

      if (imageFile) {
        formData.append("UploadedImageName", imageFile);
      }

      if (isUpdate && payload.ID) {
        await updateSubject(token, formData, payload.ID);  // Pass ID as third parameter
      } else {
        await addSubject(token, formData);
      }

      await fetchSubjects();
      setIsDrawerOpen(false);
      setSubjectToEdit(null);
    } catch (error) {
      console.error("Failed to save subject:", error);
      throw error;
    }
  };

  const handleEdit = (subject) => {
    toggleDrawer(subject);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this subject?')) {
      try {
        const token = getToken();
        await deleteSubject(token, id);
        await fetchSubjects();
      } catch (error) {
        console.error('Failed to delete subject:', error);
      }
    }
  };

  // Filter and pagination
  const filteredSubjects = subjects.filter(subject =>
    subject.SubjectName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredSubjects.length / showEntries);
  const startIndex = (currentPage - 1) * showEntries;
  const endIndex = startIndex + showEntries;
  const currentSubjects = filteredSubjects.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, showEntries]);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 3;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 2) {
        pages.push(1, 2, 3);
      } else if (currentPage >= totalPages - 1) {
        pages.push(totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(currentPage - 1, currentPage, currentPage + 1);
      }
    }

    return pages;
  };

  const getImageUrl = (subject) => {
    if (subject.SystemGeneratedImageName) {
      return `${process.env.REACT_APP_API_BASE_URL || 'https://api.soksan-dss.com'}/uploads/${subject.SystemGeneratedImageName}`;
    }
    return null;
  };

  return (
    <>
      <style>{customStyles}</style>
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-nowrap gap-2">
            <span className="text-sm fw-medium text-secondary-light mb-0">Show</span>
            <select
              className="form-select form-select-sm"
              value={showEntries}
              onChange={(e) => setShowEntries(parseInt(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={-1}>All</option>
            </select>

            <div className="navbar-search">
              <form
                className="navbar-search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              >
                <input type="text" className="bg-base h-40-px w-auto" name="search" placeholder="Search subjects" />
                <Icon icon="ion:search-outline" className="icon" />
              </form>
            </div>
          </div>
          <button
            onClick={() => toggleDrawer()}
            className="btn btn-yellow text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
            Add New Subject
          </button>
        </div>

        <div className="card-body p-24">
          {loading ? (
            <div className="text-center py-4">Loading subjects...</div>
          ) : (
            <>
              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">Image</th>
                      <th scope="col">Subject Name</th>
                      <th scope="col" className="text-center">Status</th>
                      <th scope="col" className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentSubjects.map((subject) => (
                      <tr key={subject.ID}>
                        <td>
                          {getImageUrl(subject) ? (
                            <img
                              src={getImageUrl(subject)}
                              alt={subject.SubjectName}
                              className="table-image"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = '<div class="image-placeholder">No Image</div>';
                              }}
                            />
                          ) : (
                            <div className="image-placeholder">No Image</div>
                          )}
                        </td>
                        <td>
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {subject.SubjectName}
                          </span>
                        </td>
                        <td className="text-center">
                          <span
                            className={`px-24 py-4 radius-4 fw-medium text-sm ${subject.IsActive
                                ? 'bg-success-focus text-success-600 border border-success-main'
                                : 'bg-neutral-200 text-neutral-600 border border-neutral-400'
                              }`}
                          >
                            {subject.IsActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="d-flex align-items-center gap-10 justify-content-center position-relative">
                            <button
                              type="button"
                              className="edit-btn bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => handleEdit(subject)}
                            >
                              <Icon icon="lucide:edit" className="menu-icon" />
                            </button>
                            {user?.IsDeleteAllowed && (
                              <button
                                type="button"
                                className="delete-btn bg-danger-focus text-danger-600 bg-hover-danger-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                onClick={() => handleDelete(subject.ID)}
                              >
                                <Icon icon="lucide:trash-2" className="menu-icon" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredSubjects.length === 0 && !loading && (
                <div className="text-center py-4 text-muted">
                  No subjects found
                </div>
              )}

              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                <span>
                  Showing {filteredSubjects.length === 0 ? 0 : startIndex + 1} to {Math.min(endIndex, filteredSubjects.length)} of {filteredSubjects.length} entries
                </span>
                {totalPages > 1 && (
                  <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
                    <li className="page-item">
                      <button
                        className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                      >
                        <Icon icon="ep:d-arrow-left" />
                      </button>
                    </li>

                    {getPageNumbers().map(page => (
                      <li key={page} className="page-item">
                        <button
                          className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${currentPage === page ? 'bg-primary-600 text-white' : ''
                            }`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      </li>
                    ))}

                    <li className="page-item">
                      <button
                        className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                      >
                        <Icon icon="ep:d-arrow-right" />
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <AddSubject
        isOpen={isDrawerOpen}
        onClose={() => {
          setSubjectToEdit(null);
          setIsDrawerOpen(false);
        }}
        onSubmit={handleSubmit}
        subjectToEdit={subjectToEdit}
      />
    </>
  );
};

export default SubjectList;